import * as React from 'react'
import * as actions from '../../../../../actions'
import * as events from '../../../../../events'

import { EmailField, PhoneField } from '@doordash/component-fields'
import { Text, Stack, StackChildren } from '@doordash/design-language'
import * as Exp from '../../../../../experimentConstants'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import { Interpolate } from '@doordash/lib-i18n'
import { i18nKeys } from 'translationTypes'
import { RETURN_TO_APPLICATION_AUTO } from '../../../../../experimentConstants'
import i18n from '../../../../../i18n'
import { push, Steps } from '../../../../../utilities/steps'
import {
  Container,
  H3,
  LegalLink,
  LegalText,
  SplitSection,
  TitleWrapper,
} from './styles'

import Button from '@doordash/component-button'
import { get, has, noop, values } from 'lodash'
import { connect } from 'react-redux'
import { getLocationFromZip } from '../../../../../api'
import ZipField from '../../../../../components/molecules/zip-field'
import {
  AlreadyStartedEnum,
  AlreadyStartedSigningUpModal,
} from '../../../../../models/types'
import * as DA from '../../../../../typeModules/events'
import Form from '../../../../../utilities/Form'
import formatPhoneNumber from '../../../../../utilities/formatPhoneNumber'
// tslint:disable-next-line:max-line-length
import { validators as sharedValidators } from '../../../../../utilities/validators'
import StartedSigningUpModal from '../StartedSigningUpModal'
import t from './strings'

import { SignupForm as Tag } from '../../../../../models/selectorAnchors'
import SlideForm from './SlideForm'

const styles: any = {}

type SignupFormProps = {
  onSubmit?: (any) => Promise<any>
  isContained?: boolean
  title?: string
  referralSubtitle?: string
  validators?: Record<string, any>
  phoneCountry?: string
  guaranteedEarningsCode?: string
  trackDasherApply?: (data: DA.DasherApply) => Promise<any>
  toggleAlreadyStartedSigningUpModal?: (
    data?: AlreadyStartedSigningUpModal
  ) => Promise<any>
  trackDasherContinueApplication?: (
    data: DA.DasherContinueApplication
  ) => Promise<any>
  alreadyStartedSigningUpModal?: AlreadyStartedSigningUpModal
  experiments?: any
}
export class SignupForm extends React.Component<SignupFormProps> {
  public static fields: any = {
    email: '',
    phoneNumber: '',
    zipCode: '',
  }

  public static validators: any = {
    email: sharedValidators.email,
    phoneNumber: sharedValidators.hidden,
    zipCode: sharedValidators.hidden,
  }

  public static defaultProps = {
    trackDasherApply: noop,
    onSubmit: noop,
    isContained: false,
    title: t.title(),
    phoneCountry: 'US',
    toggleAlreadyStartedSigningUpModal: noop,
    alreadyStartedSigningUpModal: {
      isShowing: false,
      email: null,
      phoneNumber: null,
      existingCredentials: false,
    },
    trackDasherContinueApplication: noop,
  }

  public state = {
    showingContinueModal: false,
    isShortForm: false,
    nationalPhoneNumber: '',
    phoneCountry: '',
    phoneErrors: '',
  }

  public toggleModal = () => {
    const {
      toggleAlreadyStartedSigningUpModal,
      trackDasherContinueApplication,
    } = this.props

    trackDasherContinueApplication({
      action: DA.EventActions.Clicked,
      name: AlreadyStartedEnum.Default,
    })

    toggleAlreadyStartedSigningUpModal({
      type: AlreadyStartedEnum.Default,
    })
  }

  public closeModal = () => {
    const {
      toggleAlreadyStartedSigningUpModal,
      trackDasherContinueApplication,
    } = this.props
    trackDasherContinueApplication({
      action: DA.EventActions.Clicked,
      name: AlreadyStartedEnum.Closed,
    })

    toggleAlreadyStartedSigningUpModal({
      type: AlreadyStartedEnum.Default,
    })
  }

  public handleSubmit = async data => {
    const {
      onSubmit,
      guaranteedEarningsCode,
      trackDasherApply,
      toggleAlreadyStartedSigningUpModal,
    } = this.props
    const { isShortForm, nationalPhoneNumber } = this.state
    const { ...requestedData } = data

    if (!isShortForm) {
      SignupForm.validators = {
        email: sharedValidators.hidden,
        phoneNumber: sharedValidators.phoneNumber,
        zipCode: sharedValidators.zipCode,
      }

      trackDasherApply({
        action: DA.EventActions.Clicked,
        name: DA.DasherApplyNames.GetStarted,
        email: data.email,
      })

      this.setState({ isShortForm: true })

      return
    }

    let payload = { ...requestedData }

    if (guaranteedEarningsCode) {
      payload = { guaranteedEarningsCode, ...payload }
    }

    trackDasherApply({
      action: DA.EventActions.Clicked,
      name: DA.DasherApplyNames.SignUp,
      email: data.email,
      phoneNumber: data.phoneNumber,
      zipCode: data.zipCode,
    })

    try {
      const result = await onSubmit(payload)
      push(result.uniqueLink, Steps.ProfileSetup)
    } catch (errors) {
      const error = values(get(errors, 'fieldErrors', ''))[0]
      trackDasherApply({
        action: DA.EventActions.Error,
        name: DA.DasherApplyNames.SignUp,
        value: error,
        email: data.email,
        phoneNumber: data.phoneNumber,
        zipCode: data.zipCode,
      })

      if (isFeatureFlagEnabled(RETURN_TO_APPLICATION_AUTO)) {
        if (
          has(errors, 'fieldErrors.email') ||
          has(errors, 'fieldErrors.phoneNumber')
        ) {
          toggleAlreadyStartedSigningUpModal({
            isShowing: true,
            email: data.email,
            phoneNumber: nationalPhoneNumber,
            type: AlreadyStartedEnum.ExistingCredentials,
          })
        }
      }

      // If an error is thrown on email, go back to the
      // first form. Otherwise, stay.
      if (has(errors, 'fieldErrors.email')) {
        this.setState({ isShortForm: false })
      }

      if (has(errors, 'fieldErrors.phoneNumber')) {
        this.setState({
          phoneErrors: `Invalid Phone Number: ${errors.fieldErrors.phoneNumber}`,
        })
      }

      throw errors
    }
  }

  public handleGetStarted = () => {
    this.setState({ getStarted: true })
  }

  public handleZipCode = async zipCode => {
    const address = await getLocationFromZip(zipCode)
    return address
  }

  public render() {
    const {
      isContained,
      title,
      referralSubtitle,
      alreadyStartedSigningUpModal,
    } = this.props
    const { isShortForm } = this.state
    const Root = isContained ? Container : 'div'

    const legalICA = '/dasher/us/ica/'
    if (isFeatureFlagEnabled(Exp.DX_CRO_LP_GRADUATION_Q3_2020)) {
      return <SlideForm />
    }

    return (
      <Root data-anchor-id={Tag.Root}>
        {alreadyStartedSigningUpModal.isShowing && (
          <StartedSigningUpModal onClose={this.closeModal} />
        )}
        <StackChildren>
          <TitleWrapper data-anchor-id={Tag.Title}>
            <H3>
              <Text weight={Text.Weights.Bold} size={Text.Sizes.S18}>
                {isShortForm ? t.finishSigningUp() : title}
              </Text>
            </H3>
          </TitleWrapper>
          <TitleWrapper data-anchor-id={Tag.ReferralSubtitle}>
            <Text>{referralSubtitle}</Text>
          </TitleWrapper>
          <Form
            onSubmit={this.handleSubmit}
            validators={SignupForm.validators}
            fields={SignupForm.fields}
          >
            {(fields, handleChange, handleSubmit, { isLoading }) => (
              <form className={styles.formContent} onSubmit={handleSubmit}>
                <StackChildren>
                  {!isShortForm && (
                    <EmailField
                      type="email"
                      placeholder={t.email()}
                      onChange={handleChange.bind(this, 'email')}
                      autoComplete="email"
                      label="Email"
                      isLabelHidden
                      data-anchor-id={Tag.Email}
                      {...fields.email}
                    />
                  )}
                  {isShortForm && (
                    <StackChildren>
                      <PhoneField
                        isInternationalEnabled
                        type="tel"
                        placeholder={t.phone()}
                        onChange={(unformatted, formatted) => {
                          this.setState({
                            nationalPhoneNumber: unformatted,
                            phoneErrors: '',
                          })
                          handleChange('phoneNumber', formatted.number.number)
                        }}
                        onCountryChange={(
                          countrySelected,
                          unformatted,
                          formatted
                        ) => {
                          this.setState({
                            phoneCountry: countrySelected,
                            nationalPhoneNumber: unformatted,
                            phoneErrors: '',
                          })
                          handleChange('phoneNumber', formatted.number.number)
                        }}
                        autoComplete="tel"
                        label="Phone Number"
                        isLabelHidden
                        data-anchor-id={Tag.Phone}
                        value={this.state.nationalPhoneNumber}
                        currentCountry={
                          this.state.phoneCountry
                            ? this.state.phoneCountry
                            : this.props.phoneCountry
                        }
                        error={
                          this.state.phoneErrors || fields.phoneNumber.error
                        }
                      />
                      <ZipField
                        type="zip"
                        placeholder={t.zipcode()}
                        onChange={handleChange.bind(this, 'zipCode')}
                        getLocation={this.handleZipCode}
                        label="Zipcode"
                        isLabelHidden
                        anchorId={Tag.Zip}
                        {...fields.zipCode}
                      />
                    </StackChildren>
                  )}
                  <Stack>
                    <LegalText
                      lineHeight={Text.LineHeights.Medium}
                      size={Text.Sizes.S12}
                      weight={Text.Weights.Regular}
                      data-anchor-id={Tag.LegalText}
                    >
                      <Interpolate
                        i18n={i18n}
                        i18nKey={
                          isShortForm
                            ? i18nKeys.signupForm__legalSignup
                            : i18nKeys.signupForm__legal
                        }
                        components={[
                          <LegalLink href={legalICA} target="_blank">
                            ## Independent Contractor Agreement##
                          </LegalLink>,
                          <LegalLink
                            href="/dasherprivacypolicy/"
                            target="_blank"
                          >
                            ##Dasher Privacy Policy##
                          </LegalLink>,
                        ]}
                      />
                    </LegalText>
                  </Stack>
                  <Button
                    state={
                      isLoading ? Button.States.Loading : Button.States.Default
                    }
                    onClick={async () => {
                      try {
                        await handleSubmit()
                      } catch (e) {
                        console.warn(e)
                      }
                    }}
                    type={Button.Types.PrimaryPill}
                    data-anchor-id={Tag.PrimaryAction}
                  >
                    {isShortForm ? t.signUp() : t.getStarted()}
                  </Button>
                </StackChildren>
                <Button
                  type={Button.Types.FlatPrimary}
                  onClick={this.toggleModal}
                  data-anchor-id={Tag.AlreadySignedUp}
                >
                  {t.alreadySignedUp()}
                </Button>
              </form>
            )}
          </Form>
        </StackChildren>
      </Root>
    )
  }
}

const mapStateToProps = state => ({
  guaranteedEarningsCode: state.guaranteedEarningsCode,
  alreadyStartedSigningUpModal: state.alreadyStartedSigningUpModal,
  experiments: state.experiments,
  phoneCountry: state.country,
})

const mapDispatchToProps = {
  onSubmit: actions.submitDasherApplication,
  trackDasherContinueApplication: events.trackDasherContinueApplication,
  trackDasherApply: events.trackDasherApply,
  toggleAlreadyStartedSigningUpModal:
    actions.toggleAlreadyStartedSigningUpModal,
}

export default connect(mapStateToProps, mapDispatchToProps)(SignupForm)
